import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type PointerEvent,
} from "react";
import { WORKSPACE_MIN_PANEL_PX } from "src-next/modules/clinical-insights/constants/clinicalInsightsTheme";

const STORAGE_KEY = "clinical-insights-workspace-left-percent-v2";
const MIN_LEFT_PERCENT = 28;
const MAX_LEFT_PERCENT = 72;

/** Fallback antes do container medir; o layout ajusta para o mínimo em px. */
const DEFAULT_LEFT_PERCENT = MIN_LEFT_PERCENT;

function minLeftPercentForWidth(containerWidthPx: number): number {
  if (containerWidthPx <= 0) return DEFAULT_LEFT_PERCENT;
  return Math.max(MIN_LEFT_PERCENT, (WORKSPACE_MIN_PANEL_PX / containerWidthPx) * 100);
}

function maxLeftPercentForWidth(containerWidthPx: number): number {
  if (containerWidthPx <= 0) return MAX_LEFT_PERCENT;
  return Math.min(
    MAX_LEFT_PERCENT,
    ((containerWidthPx - WORKSPACE_MIN_PANEL_PX) / containerWidthPx) * 100
  );
}

function readStoredPercent(): { value: number; fromStorage: boolean } {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return { value: DEFAULT_LEFT_PERCENT, fromStorage: false };
    const n = Number.parseFloat(raw);
    if (!Number.isFinite(n)) return { value: DEFAULT_LEFT_PERCENT, fromStorage: false };
    return {
      value: Math.min(MAX_LEFT_PERCENT, Math.max(MIN_LEFT_PERCENT, n)),
      fromStorage: true,
    };
  } catch {
    return { value: DEFAULT_LEFT_PERCENT, fromStorage: false };
  }
}

export function useInsightWorkspaceSplit() {
  const containerRef = useRef<HTMLDivElement>(null);
  const initial = readStoredPercent();
  const [leftPercent, setLeftPercent] = useState(initial.value);
  const draggingRef = useRef(false);
  const skipInitialMinSnap = useRef(initial.fromStorage);

  useLayoutEffect(() => {
    if (skipInitialMinSnap.current) return;
    const container = containerRef.current;
    if (!container) return;
    const rect = container.getBoundingClientRect();
    if (rect.width <= 0) return;
    const minPct = minLeftPercentForWidth(rect.width);
    setLeftPercent(minPct);
    skipInitialMinSnap.current = true;
  }, []);

  const applyPercentFromPointer = useCallback((clientX: number) => {
    const container = containerRef.current;
    if (!container) return;
    const rect = container.getBoundingClientRect();
    if (rect.width <= 0) return;
    const ratio = ((clientX - rect.left) / rect.width) * 100;
    const minPct = minLeftPercentForWidth(rect.width);
    const maxPct = maxLeftPercentForWidth(rect.width);
    const next = Math.min(maxPct, Math.max(minPct, ratio));
    setLeftPercent(next);
  }, []);

  const onDividerPointerDown = useCallback(
    (event: PointerEvent<HTMLDivElement>) => {
      event.preventDefault();
      draggingRef.current = true;
      event.currentTarget.setPointerCapture(event.pointerId);
      applyPercentFromPointer(event.clientX);
    },
    [applyPercentFromPointer]
  );

  const onDividerPointerMove = useCallback(
    (event: PointerEvent<HTMLDivElement>) => {
      if (!draggingRef.current) return;
      applyPercentFromPointer(event.clientX);
    },
    [applyPercentFromPointer]
  );

  const onDividerPointerUp = useCallback((event: PointerEvent<HTMLDivElement>) => {
    if (!draggingRef.current) return;
    draggingRef.current = false;
    try {
      event.currentTarget.releasePointerCapture(event.pointerId);
    } catch {
      /* ignore */
    }
  }, []);

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, String(leftPercent));
    } catch {
      /* ignore */
    }
  }, [leftPercent]);

  return {
    containerRef,
    leftPercent,
    onDividerPointerDown,
    onDividerPointerMove,
    onDividerPointerUp,
  };
}
